import { useState, useEffect } from 'react';
import { ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export function PhotoLikeButton({ photo, shareToken, size = 'default', showCount = true, className = '' }) {
  const [likes, setLikes] = useState(photo?.likes || 0);
  const [hasLiked, setHasLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  // Check if user has already liked this photo (stored in localStorage)
  useEffect(() => {
    const likedPhotos = JSON.parse(localStorage.getItem('liked_photos') || '{}');
    setHasLiked(!!likedPhotos[photo?.id]);
    if (photo?.likes !== undefined) {
      setLikes(photo.likes);
    }
  }, [photo?.id, photo?.likes]);

  const handleLike = async (e) => {
    e.stopPropagation();

    if (hasLiked || isLiking) return;

    setIsLiking(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/photo/${photo.id}/like`
      );

      if (response.data.success) {
        setLikes(response.data.likes);
        setHasLiked(true);

        // Store in localStorage to prevent duplicate likes
        const likedPhotos = JSON.parse(localStorage.getItem('liked_photos') || '{}');
        likedPhotos[photo.id] = true;
        localStorage.setItem('liked_photos', JSON.stringify(likedPhotos));
      }
    } catch (error) {
      console.error('Failed to like photo:', error);
      // Still update UI optimistically on error (might be rate limited)
      if (error.response?.status === 429) {
        // Already liked or rate limited
        setHasLiked(true);
        const likedPhotos = JSON.parse(localStorage.getItem('liked_photos') || '{}');
        likedPhotos[photo.id] = true;
        localStorage.setItem('liked_photos', JSON.stringify(likedPhotos));
      }
    } finally {
      setIsLiking(false);
    }
  };

  const buttonSize = size === 'sm' ? 'h-8 w-8' : 'h-10 px-3';
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <Button
      variant="ghost"
      size={size === 'sm' ? 'icon' : 'sm'}
      onClick={handleLike}
      disabled={hasLiked || isLiking}
      className={`${buttonSize} ${
        hasLiked
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
          : 'bg-white/20 hover:bg-white/30 text-white'
      } transition-all duration-200 ${className}`}
      title={hasLiked ? 'You liked this photo' : 'Like this photo'}
    >
      <ThumbsUp
        className={`${iconSize} ${hasLiked ? 'fill-current animate-scale-in' : ''} ${isLiking ? 'animate-pulse' : ''}`}
      />
      {showCount && likes > 0 && (
        <span className="ml-1 text-xs font-medium">{likes}</span>
      )}
    </Button>
  );
}

export default PhotoLikeButton;
